import React, { useState } from 'react';
import {
  X,
  Video,
  Users,
  Lock,
  Shield,
  FileText,
  Sparkles,
  Calendar,
  Clock
} from 'lucide-react';

const CreateRoomModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_participants: 10,
    require_password: false,
    password: '',
    start_immediately: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onCreate(formData);
    } catch (error) {
      console.error('Error creating room:', error);
    } finally {
      setLoading(false);
    }
  };

  const participantOptions = [
    { value: 5, label: '5 participants', description: 'Small team meeting' },
    { value: 10, label: '10 participants', description: 'Team standup' },
    { value: 20, label: '20 participants', description: 'Department meeting' },
    { value: 50, label: '50 participants', description: 'Large conference' }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-virgo-navy-700 to-virgo-navy-800 rounded-2xl shadow-2xl w-full max-w-lg border border-virgo-navy-600 overflow-hidden">
        {/* Header with gradient accent */}
        <div className="relative px-6 py-5 border-b border-virgo-navy-600">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-indigo-500/10 to-cyan-500/10" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent">
                  Create Meeting
                </h2>
                <p className="text-sm text-virgo-teal-400">Set up your video conference</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-virgo-teal-400 hover:text-white hover:bg-virgo-navy-600 rounded-lg transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Meeting Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-virgo-teal-300 mb-2">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              Meeting Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
              placeholder="e.g., Team Standup, Client Deposition"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-virgo-teal-300 mb-2">
              <FileText className="w-4 h-4 text-cyan-400" />
              Description
              <span className="text-virgo-navy-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
              placeholder="Brief description of the meeting purpose..."
              rows={3}
            />
          </div>

          {/* Max Participants */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-virgo-teal-300 mb-2">
              <Users className="w-4 h-4 text-cyan-400" />
              Maximum Participants
            </label>
            <div className="grid grid-cols-2 gap-3">
              {participantOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, max_participants: option.value })}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    formData.max_participants === option.value
                      ? 'bg-cyan-500/20 border-cyan-500 text-white'
                      : 'bg-virgo-navy-800 border-virgo-navy-600 text-virgo-teal-300 hover:border-virgo-navy-500'
                  }`}
                >
                  <div className="font-semibold">{option.value}</div>
                  <div className="text-xs text-virgo-teal-500">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Password Protection */}
          <div className="bg-virgo-navy-800/50 rounded-xl p-4 border border-virgo-navy-600">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  formData.require_password
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-virgo-navy-700 text-virgo-navy-400'
                }`}>
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-white">Password Protection</div>
                  <div className="text-sm text-virgo-teal-500">Require a password to join</div>
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.require_password}
                  onChange={(e) => setFormData({ ...formData, require_password: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-virgo-navy-600 rounded-full peer peer-checked:bg-cyan-500 transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
              </div>
            </label>

            {formData.require_password && (
              <div className="mt-4 pt-4 border-t border-virgo-navy-600">
                <label className="flex items-center gap-2 text-sm font-medium text-virgo-teal-300 mb-2">
                  <Lock className="w-4 h-4 text-amber-400" />
                  Meeting Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-virgo-navy-900 border border-virgo-navy-600 rounded-xl text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  placeholder="Enter a secure password"
                  required={formData.require_password}
                />
              </div>
            )}
          </div>

          {/* Start Options */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, start_immediately: true })}
              className={`flex-1 p-4 rounded-xl border flex items-center gap-3 transition-all ${
                formData.start_immediately
                  ? 'bg-green-500/20 border-green-500 text-white'
                  : 'bg-virgo-navy-800 border-virgo-navy-600 text-virgo-teal-300 hover:border-virgo-navy-500'
              }`}
            >
              <Clock className={`w-5 h-5 ${formData.start_immediately ? 'text-green-400' : 'text-virgo-teal-500'}`} />
              <div className="text-left">
                <div className="font-medium">Start Now</div>
                <div className="text-xs text-virgo-teal-500">Begin immediately</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, start_immediately: false })}
              className={`flex-1 p-4 rounded-xl border flex items-center gap-3 transition-all ${
                !formData.start_immediately
                  ? 'bg-blue-500/20 border-blue-500 text-white'
                  : 'bg-virgo-navy-800 border-virgo-navy-600 text-virgo-teal-300 hover:border-virgo-navy-500'
              }`}
            >
              <Calendar className={`w-5 h-5 ${!formData.start_immediately ? 'text-blue-400' : 'text-virgo-teal-500'}`} />
              <div className="text-left">
                <div className="font-medium">Create Room</div>
                <div className="text-xs text-virgo-teal-500">Join later</div>
              </div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-virgo-navy-600 text-virgo-teal-300 rounded-xl hover:bg-virgo-navy-500 transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-xl hover:from-cyan-600 hover:to-indigo-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Video className="w-5 h-5" />
                  {formData.start_immediately ? 'Create & Join' : 'Create Room'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoomModal;
